import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Sparkles, BookOpen, Users, Gamepad2, Award, Heart } from "lucide-react";
import SEOFooter from "@/components/SEOFooter";
export default function About() {
  return <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Game
            </Button>
          </Link>
          <h1 className="text-xl font-bold text-primary">67 CHAOS</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.article initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-8">
          {/* Hero Section */}
          <section className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight">
              About <span className="text-primary">67 CHAOS</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              The story behind the viral party game that celebrates Dictionary.com's 
              2025 Word of the Year
            </p>
          </section>

          {/* The 67 Meme */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-primary" />
              What is the 67 Meme?
            </h2>
            <Card className="p-6 bg-primary/10 border-primary/30">
              <p className="text-lg text-muted-foreground mb-4">
                The number 67 became an internet phenomenon in late 2024, spreading 
                virally across TikTok, YouTube Shorts, and Gen Alpha social media. 
                What started as an inside joke evolved into a cultural moment that 
                captured the chaotic, collaborative spirit of a new generation.
              </p>
              <p className="text-lg text-muted-foreground mb-4">
                The meme typically involves groups of people holding up fingers to 
                show "6" and "7" together, symbolizing unity, teamwork, and playful 
                chaos. It's become a rallying cry, a greeting, and a shared experience 
                among millions of young internet users.
              </p>
              <p className="text-lg text-muted-foreground">
                In recognition of its cultural impact, <strong>Dictionary.com officially 
                named "67" the 2025 Word of the Year</strong>, cementing its place in 
                internet history alongside terms like "rizz" and "no cap."
              </p>
            </Card>
          </section>

          {/* Why We Built This */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Gamepad2 className="h-8 w-8 text-primary" />
              Why We Built 67 CHAOS
            </h2>
            <p className="text-lg text-muted-foreground">
              We saw the 67 meme taking over the internet and asked ourselves: 
              "What if we could turn this viral moment into an actual game?"
            </p>
            <p className="text-lg text-muted-foreground">
              67 CHAOS was born from the idea that reaching "67" together should 
              feel just as chaotic and collaborative as the meme itself. We wanted 
              to create a game that:
            </p>
            <ul className="space-y-3 text-lg text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>Captures the essence of collective chaos that defines the 67 meme</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>Creates shareable, meme-worthy moments every game</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>Is instantly playable — no downloads, no accounts, just fun</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>Works perfectly on mobile where Gen Alpha lives</span>
              </li>
            </ul>
          </section>

          {/* Dictionary.com Recognition */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              Dictionary.com 2025 Word of the Year
            </h2>
            <Card className="p-6 bg-secondary/50 border-border/50">
              <blockquote className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4">
                "67 represents more than a number — it's a symbol of collective joy, 
                collaborative humor, and the way Gen Alpha creates shared cultural moments. 
                The term's rapid spread across platforms demonstrates the power of 
                participatory internet culture."
              </blockquote>
              <p className="text-sm text-muted-foreground mt-4">
                — Dictionary.com, December 2024
              </p>
            </Card>
            <p className="text-lg text-muted-foreground">
              This recognition validates what millions already knew: 67 isn't just a 
              number, it's a movement. Our game celebrates this by making the journey 
              to 67 a collaborative adventure that can only be won together.
            </p>
          </section>

          {/* The Team */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              About KUL Games
            </h2>
            <p className="text-lg text-muted-foreground">
              KUL Games is a small indie game studio focused on creating social, 
              browser-based games that bring people together. We believe the best 
              games are the ones you can play instantly with friends, no matter 
              what device you're on.
            </p>
            <p className="text-lg text-muted-foreground">
              67 CHAOS is our love letter to internet culture — a game that's as 
              chaotic, fun, and shareable as the meme that inspired it.
            </p>
          </section>

          {/* Features Highlight */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Award className="h-8 w-8 text-primary" />
              What Makes 67 CHAOS Special
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">🎮 Instant Play</h3>
                <p className="text-muted-foreground text-sm">
                  No downloads, no accounts. Just open the link and start playing in seconds.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">📱 Mobile-First</h3>
                <p className="text-muted-foreground text-sm">
                  Designed for phones with touch controls, haptic feedback, and responsive design.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">🔊 Dynamic Audio</h3>
                <p className="text-muted-foreground text-sm">
                  Lo-fi beats that intensify as you approach 67, plus satisfying sound effects.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">🎉 Viral Animations</h3>
                <p className="text-muted-foreground text-sm">
                  TikTok-worthy victory effects with the iconic "6 7" animation.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">📊 XP & Progression</h3>
                <p className="text-muted-foreground text-sm">
                  Level up, unlock cosmetics, track streaks, and complete daily challenges.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">🔗 Easy Sharing</h3>
                <p className="text-muted-foreground text-sm">
                  Share games via WhatsApp, SMS, or deep links. Generate result cards for social media.
                </p>
              </Card>
            </div>
          </section>

          {/* Community */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Heart className="h-8 w-8 text-primary" />
              Join the 67 Community
            </h2>
            <p className="text-lg text-muted-foreground">
              Share your best game moments with #67chaos and #SixSeven on social media!
            </p>
          </section>

          {/* FAQ Section for AEO */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">Is 67 CHAOS really free?</h3>
                <p className="text-muted-foreground">
                  Yes! 67 CHAOS is 100% free to play. No hidden costs, no in-app purchases. 
                  Just pure chaotic fun.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">Do I need to create an account?</h3>
                <p className="text-muted-foreground">
                  No account required! Just enter your name and start playing. Your progress 
                  is saved locally on your device.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">Can I play alone?</h3>
                <p className="text-muted-foreground">
                  Yes! Practice Mode lets you play solo against an AI opponent to learn 
                  the game mechanics before challenging friends.
                </p>
              </Card>
              <Card className="p-4 bg-card/50">
                <h3 className="font-bold text-lg mb-2">What browsers are supported?</h3>
                <p className="text-muted-foreground">
                  67 CHAOS works on all modern browsers including Chrome, Safari, Firefox, 
                  and Edge — on both desktop and mobile.
                </p>
              </Card>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center py-8">
            <Link to="/">
              <Button size="lg" className="h-16 px-12 text-xl font-bold glow-green">
                Play 67 CHAOS Now 
              </Button>
            </Link>
          </section>
        </motion.article>
      </main>

      <SEOFooter />
    </div>;
}